import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import axios from 'axios'

const MinutesManagement = () => {
  const [minutes, setMinutes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingMinute, setEditingMinute] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedFile, setSelectedFile] = useState(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm()

  // Fetch minutes
  const fetchMinutes = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await axios.get('/api/minutes')
      setMinutes(response.data)
    } catch (err) {
      setError('Failed to load board minutes. Please try again.')
      console.error('Error fetching minutes:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMinutes()
  }, [])

  // Open modal for creating/editing
  const openModal = (minute = null) => {
    setEditingMinute(minute)
    setSelectedFile(null)
    setUploadProgress(0)
    if (minute) {
      // Format date for input field
      const formattedMinute = {
        ...minute,
        meetingDate: minute.meetingDate
          ? new Date(minute.meetingDate).toISOString().split('T')[0]
          : '',
      }
      reset(formattedMinute)
    } else {
      // Get current fiscal year (July 1 - June 30)
      const now = new Date()
      const currentYear = now.getFullYear()
      const fiscalYear = now.getMonth() >= 6 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`

      reset({
        title: '',
        meetingDate: '',
        fiscalYear: fiscalYear,
        summary: '',
        meetingType: 'monthly',
      })
    }
    setShowModal(true)
  }

  // Close modal
  const closeModal = () => {
    setShowModal(false)
    setEditingMinute(null)
    setSelectedFile(null)
    setUploadProgress(0)
    reset()
  }

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file')
        e.target.value = ''
        return
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB')
        e.target.value = ''
        return
      }
      setSelectedFile(file)
    }
  }

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setSubmitting(true)
      setUploadProgress(0)

      const formData = new FormData()
      formData.append('title', data.title)
      formData.append('meetingDate', data.meetingDate)
      formData.append('fiscalYear', data.fiscalYear)
      formData.append('summary', data.summary || '')
      formData.append('meetingType', data.meetingType)

      if (selectedFile) {
        formData.append('file', selectedFile)
      }

      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          setUploadProgress(percentCompleted)
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }

      if (editingMinute) {
        // Update existing minute
        await axios.put(`/api/minutes/${editingMinute._id}`, formData, config)
      } else {
        // Create new minute
        await axios.post('/api/minutes', formData, config)
      }

      await fetchMinutes()
      closeModal()
    } catch (err) {
      alert(
        `Failed to ${editingMinute ? 'update' : 'upload'} minutes: ${
          err.response?.data?.message || err.message
        }`
      )
    } finally {
      setSubmitting(false)
      setUploadProgress(0)
    }
  }

  // Handle delete
  const handleDelete = async (minuteId) => {
    if (!window.confirm('Are you sure you want to delete these minutes?')) return

    try {
      await axios.delete(`/api/minutes/${minuteId}`)
      await fetchMinutes()
    } catch (err) {
      alert('Failed to delete minutes: ' + (err.response?.data?.message || err.message))
    }
  }

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Get meeting type badge
  const getMeetingTypeBadge = (type) => {
    const badges = {
      monthly: 'bg-primary-100 text-primary-800',
      special: 'bg-secondary-100 text-secondary-800',
      annual: 'bg-tertiary-100 text-tertiary-800',
    }
    return badges[type?.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Board Minutes Management</h1>
          <p className="text-gray-600 mt-1">Upload and manage board meeting minutes</p>
        </div>
        <button
          onClick={() => openModal()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium flex items-center shadow-lg"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          Upload Minutes
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Minutes List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : minutes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No board minutes</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by uploading the first meeting minutes.
          </p>
          <div className="mt-6">
            <button
              onClick={() => openModal()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              Upload Minutes
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meeting Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meeting Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uploaded
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {minutes.map((minute) => (
                  <tr key={minute._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <svg
                            className="w-6 h-6 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {minute.title}
                          </div>
                          {minute.summary && (
                            <div className="text-sm text-gray-500 line-clamp-1">
                              {minute.summary}
                            </div>
                          )}
                          {minute.fiscalYear && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              FY {minute.fiscalYear}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {minute.meetingDate ? formatDate(minute.meetingDate) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getMeetingTypeBadge(
                          minute.meetingType
                        )}`}
                      >
                        {minute.meetingType?.charAt(0).toUpperCase() +
                          minute.meetingType?.slice(1) || 'Monthly'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {minute.uploadDate
                          ? new Date(minute.uploadDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        {minute.fileUrl && (
                          <a
                            href={minute.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View
                          </a>
                        )}
                        <button
                          onClick={() => openModal(minute)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(minute._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-md shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-md">
              <h3 className="text-xl font-bold text-gray-900">
                {editingMinute ? 'Edit Board Minutes' : 'Upload Board Minutes'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500 rounded-md">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  {...register('title', { required: 'Title is required' })}
                  className={`w-full px-4 py-2 border ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  } rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  placeholder="Board Meeting - January 2024"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>

              {/* Meeting Date and Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meeting Date *
                  </label>
                  <input
                    type="date"
                    {...register('meetingDate', { required: 'Meeting date is required' })}
                    className={`w-full px-4 py-2 border ${
                      errors.meetingDate ? 'border-red-300' : 'border-gray-300'
                    } rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  />
                  {errors.meetingDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.meetingDate.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meeting Type *
                  </label>
                  <select
                    {...register('meetingType', { required: 'Meeting type is required' })}
                    className={`w-full px-4 py-2 border ${
                      errors.meetingType ? 'border-red-300' : 'border-gray-300'
                    } rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="special">Special</option>
                    <option value="annual">Annual</option>
                  </select>
                  {errors.meetingType && (
                    <p className="mt-1 text-sm text-red-600">{errors.meetingType.message}</p>
                  )}
                </div>
              </div>

              {/* Hidden Fiscal Year field - auto-calculated */}
              <input
                type="hidden"
                {...register('fiscalYear')}
              />

              {/* Summary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Summary
                </label>
                <textarea
                  {...register('summary')}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="Brief summary of the meeting..."
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDF File <span className="text-gray-500 text-sm font-normal">(Optional)</span>
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-primary-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                        <span>Upload a file</span>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PDF up to 10MB</p>
                    {selectedFile && (
                      <p className="text-sm text-tertiary-600 font-medium mt-2">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                    {editingMinute && !selectedFile && editingMinute.fileName && (
                      <p className="text-sm text-gray-600 mt-2">
                        Current file: {editingMinute.fileName}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-md h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-md transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium disabled:opacity-50 flex items-center"
                >
                  {submitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      {editingMinute ? 'Update Minutes' : 'Upload Minutes'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default MinutesManagement
